/** Вкладка «Доп» кабинета оператора (23.08, владелец): допработы, назначенные
 *  на ОО-доске, наконец ПРИХОДЯТ оператору. Тот же GET /api/extra-work?date= —
 *  сервер для роли operator отдаёт только его строки. «Принять» — PUT status
 *  confirmed (роут разрешает оператору править только своё). */
import { useState } from "react";
import { api } from "../../api/client";
import { useExtraWork, useExtraWorkHistory, type ExtraWork } from "../../hooks/useExtraWork";
import { TimePicker } from "../../components/shared";
import { ExtraReportSheet } from "./ExtraReportSheet";
import { todayBusiness, nowUtc5 } from "../../utils/date";
/* 04.09: заявка на допработу (26.08) вынесена в ProposeExtraForm — та же форма в шите дашборда */
import { ProposeExtraForm } from "./ProposeExtraForm";
import css from "./extraTab.module.css";

const RU_BRANCH: Record<string, string> = { "20b": "20Б", psih: "Псих", svo: "СВО", among: "Амонг" };
/** Длительность в часах; интервал через полночь считается по суточному кольцу, равные времена — 0 (как зарплата). */
const hours = (a: string, b: string): number => {
  const [h1 = 0, m1 = 0] = a.split(":").map(Number); const [h2 = 0, m2 = 0] = b.split(":").map(Number);
  let d = h2 * 60 + m2 - (h1 * 60 + m1); if (d < 0) d += 1440;
  return d / 60;
};
/** 03.10: сумма ОО (rate_override) — ИТОГ, не ставка в час: «Монтаж видео» 00:00–00:00 за ₽1000 показывался как 24 ч × ₽1000. */
const payOf = (w: ExtraWork): number => (w.rate_override != null ? Number(w.rate_override) : Math.round(hours(w.timeFrom, w.timeTo) * w.hourlyRate));

/** Работа уже позади: прошлый день — всегда, сегодня — когда время конца минуло. */
function isDone(w: ExtraWork, date: Date): boolean {
  const today = todayBusiness();
  if (date.getTime() < today.getTime()) return true;
  if (date.getTime() > today.getTime()) return false;
  const [h, m] = w.timeTo.split(":").map(Number); const now = nowUtc5();
  return (h ?? 0) * 60 + (m ?? 0) <= now.getUTCHours() * 60 + now.getUTCMinutes();
}

/* 26.09 (владелец): учёбка «никак не оплачивается и не отображается в зарплате» — ученику
   ни суммы, ни ставки, ни отчёта. «Принял» — отдельной отметкой (POST /accept): обычное
   «Принять» ставит status=confirmed, а для учёбки это деньги, и сервер его отклонит. */
function StudyCard({ w, onChanged }: { w: ExtraWork; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <article className={`${css.card} ${css.study}`}>
      <div className={css.head}>
        <span className={css.time}>{w.timeFrom}–{w.timeTo}</span>
        {w.branchId ? <span className={css.branch}>{RU_BRANCH[w.branchId] ?? w.branchId}</span> : null}
        <span className={css.studyTag}>🎓 учёбка</span>
      </div>
      <div className={css.desc}>{w.description}</div>
      <div className={css.meta}>
        <span>{w.curatorName ? `куратор ${w.curatorName} · ` : ""}без оплаты</span>
        {w.accepted
          ? <span className={css.ok}>✓ видел</span>
          : <button type="button" className={css.accept} disabled={busy} onClick={() => {
              setBusy(true);
              void api(`/api/extra-work/${w.id}/accept`, { method: "POST" }).catch(() => {}).finally(() => { setBusy(false); onChanged(); });
            }}>Принял</button>}
      </div>
    </article>
  );
}

function ExtraCard({ w, date, onAccept, onChanged, onReport }: { w: ExtraWork; date: Date; onAccept: (id: number) => Promise<void>; onChanged: () => void; onReport: (w: ExtraWork) => void }) {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const h = hours(w.timeFrom, w.timeTo);
  const sum = payOf(w);
  const done = isDone(w, date);
  return (
    <article className={css.card}>
      <div className={css.head}>
        <span className={css.time}>{w.timeFrom}–{w.timeTo}</span>
        {w.branchId ? <span className={css.branch}>{RU_BRANCH[w.branchId] ?? w.branchId}</span> : null}
        <span className={css.sum}>₽{sum.toLocaleString("ru")}</span>
      </div>
      <div className={css.desc}>{w.description}</div>
      <div className={css.meta}>
        <span>{w.rate_override != null ? "сумма от ОО" : `${Number.isInteger(h) ? h : h.toFixed(1)} ч × ₽${w.hourlyRate}/ч`}</span>
        {w.taxi ? <span className={css.taxi}>🚕 ₽{w.taxi}</span> : null}
        {/* Часы по факту считаются после того, как ОО зачтёт расхождение — говорим об этом прямо. */}
        {w.factFrom && w.factTo && (w.factFrom !== w.timeFrom || w.factTo !== w.timeTo)
          ? <span className={w.factOk ? css.ok : css.wait}>факт {w.factFrom}–{w.factTo}{w.factOk ? " ✓" : " · ждёт ОО"}</span>
          : null}
        {/* 11.09 (владелец): «Принять» остаётся всегда — по ней в расстановке видно, что человек
            увидел работу и знает, что идти надо. Отчёт — отдельная кнопка, после неё. */}
        {w.status === "pending"
          ? <button type="button" className={css.accept} disabled={busy} onClick={() => { setBusy(true); void onAccept(w.id).finally(() => setBusy(false)); }}>Принять</button>
          : w.status === "proposed" ? <span className={css.wait}>⏳ на рассмотрении ОО</span>
          : w.status === "rework" ? <button type="button" className={css.accept} onClick={() => setPick(true)}>↩ исправить время</button>
          : w.status === "rejected" ? <span className={css.rej}>✕ отклонена</span>
          : <span className={css.ok}>✓ принято</span>}
        {done && w.status !== "rejected" && w.status !== "proposed" ? (
          <button type="button" className={`${css.accept} ${css.report}`} onClick={() => onReport(w)}>
            {w.reportedAt ? "Отчёт ✓" : "Отчёт"}
          </button>
        ) : null}
      </div>
      {pick ? <TimePicker open mode="range" step={5} label="Доп — время от и до" value={`${w.timeFrom}–${w.timeTo}`} onCommit={(v) => {
        const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/);
        if (m) void api(`/api/extra-work/${w.id}`, { method: "PUT", body: { time_from: m[1], time_to: m[2] } }).then(onChanged).catch(() => {});
        setPick(false);
      }} onCancel={() => setPick(false)} /> : null}
    </article>
  );
}

export function ExtraTab({ date, operatorId }: { date: Date; operatorId: string | null }) {
  const ew = useExtraWork(date);
  /* мультироль (fullstack/OO) получает от сервера весь день — в кабинете показываем только СВОИ */
  /* 27.09: черновик ОО («Сохранить», не отправлено) человек не видит — ЕО/ОО сервер отдаёт всё */
  const mine = ew.items.filter((w) => (!operatorId || w.operatorId === operatorId) && !w.draft);
  /* 26.08 (владелец): вся история допработ — даты, время, описание, оплата, такси */
  const [histOpen, setHistOpen] = useState(false);
  const hist = useExtraWorkHistory(histOpen);
  const histMine = hist.items.filter((w) => (!operatorId || w.operatorId === operatorId) && !w.draft);
  const accept = async (id: number): Promise<void> => {
    await api(`/api/extra-work/${id}`, { method: "PUT", body: { status: "confirmed" } }).catch(() => {});
    ew.reload();
  };
  const dm = (iso: string) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}` : "");
  const [proposing, setProposing] = useState(false);
  const [reportOf, setReportOf] = useState<ExtraWork | null>(null);
  return (
    <div className={css.root}>
      <div className={css.title}>Доп. работы
        <button type="button" className={css.plus} title="Предложить свою допработу" onClick={() => setProposing((o) => !o)}>{proposing ? "✕" : "+"}</button>
      </div>
      {proposing ? <ProposeExtraForm date={date} onDone={() => { setProposing(false); ew.reload(); }} /> : null}
      {ew.loading ? <div className={css.empty}>Загрузка…</div>
        : mine.length === 0 ? <div className={css.empty}>На этот день доп. работ нет.</div>
        : mine.map((w) => (w.kind === "training"
          ? <StudyCard key={w.id} w={w} onChanged={ew.reload} />
          : <ExtraCard key={w.id} w={w} date={date} onAccept={accept} onChanged={ew.reload} onReport={setReportOf} />))}
      <ExtraReportSheet open={!!reportOf}
        work={reportOf ? { id: reportOf.id, description: reportOf.description, timeFrom: reportOf.timeFrom, timeTo: reportOf.timeTo, taxi: reportOf.taxi, taxiPaidBy: reportOf.taxiPaidBy, factFrom: reportOf.factFrom, factTo: reportOf.factTo, reportText: reportOf.reportText } : null}
        onClose={() => setReportOf(null)}
        onDone={() => { setReportOf(null); ew.reload(); }} />
      <button type="button" className={css.histToggle} onClick={() => setHistOpen((o) => !o)}>
        📜 Вся история {histOpen ? "▴" : "▾"}
      </button>
      {histOpen ? (
        hist.loading ? <div className={css.empty}>Загрузка…</div>
          : histMine.length === 0 ? <div className={css.empty}>Доп. работ ещё не было.</div>
          : histMine.map((w) => (
            <div key={w.id} className={css.histRow}>
              <span className={css.histDate}>{dm(w.date)}</span>
              <span className={css.time}>{w.timeFrom}–{w.timeTo}</span>
              <span className={css.histDesc}>{w.description}</span>
              {w.kind === "training"
                ? <span className={css.studyTag}>🎓 учёбка</span>
                : <span className={css.sum}>₽{payOf(w).toLocaleString("ru")}</span>}
              {w.taxi && w.kind !== "training" ? <span className={css.taxi}>🚕 ₽{w.taxi}</span> : null}
            </div>
          ))
      ) : null}
    </div>
  );
}
